// AI-CONTEXT: See docs/features/msi-onderbouwing-transparency.md
import { useEffect, useState } from 'react'
import { standaardDocumentUrl } from '../helpers/meetstandaard/standaardVersions'
import adaptStandaardOnderbouwing, { isVersionedMsiEffect } from '../helpers/msiMonetisationHelpers/adaptStandaardOnderbouwing'

// The onderbouwing of a versioned MSI effect, read from the same immutable
// document its euro values are calculated with. Needed because
// useMsiOnderbouwing serves an unversioned demo document that showed another
// version's amounts (see adaptStandaardOnderbouwing).
//
// Fetched lazily — callers pass an effect only while its modal is open — and
// once per document per session: a published version never changes.

/** @type {Map<string, Promise<object>>} */
const documentCache = new Map()

const fetchDocument = (url) => {
    if (!documentCache.has(url)) {
        const promise = fetch(url).then((res) => {
            if (!res.ok) throw new Error(`Meetstandaard-document gaf status ${res.status}`)
            return res.json()
        })
        // A failure clears itself so the next open retries.
        promise.catch(() => documentCache.delete(url))
        documentCache.set(url, promise)
    }
    return documentCache.get(url)
}

const IDLE = { entry: null, meta: null, loading: false, error: null }
const LOADING = { ...IDLE, loading: true }

/**
 * @param {string} msiId - The effect's uid (`sector:slug`); '' while no modal is open
 * @param {string} standaardVersion - The version the effect is valued at (canonical)
 * @returns {{entry: object|null, meta: object|null, loading: boolean, error: Error|null}}
 */
const useStandaardOnderbouwing = (msiId, standaardVersion) => {
    const active = isVersionedMsiEffect(msiId, standaardVersion)
    // The state remembers which effect it belongs to: after a switch the
    // previous effect's onderbouwing must not show for even one render.
    // AI-CONTEXT: See docs/pitfalls.md#state-stale-derived-state-on-item-cycler-prop-change
    const requested = active ? `${msiId}@${standaardVersion}` : ''
    const [state, setState] = useState({ key: '', value: IDLE })

    useEffect(() => {
        if (!active) return undefined
        let cancelled = false

        const sector = msiId.slice(0, msiId.indexOf(':'))
        fetchDocument(standaardDocumentUrl(sector, standaardVersion))
            .then((doc) => {
                if (cancelled) return
                const adapted = adaptStandaardOnderbouwing(doc, msiId)
                if (!adapted) throw new Error(`Geen onderbouwing voor ${msiId} in ${sector} ${standaardVersion}`)
                setState({ key: requested, value: { entry: adapted.entry, meta: adapted.meta, loading: false, error: null } })
            })
            .catch((error) => {
                if (cancelled) return
                console.error('Error fetching MSI onderbouwing from the standaard document', error)
                setState({ key: requested, value: { ...IDLE, error } })
            })

        return () => { cancelled = true }
    }, [active, msiId, standaardVersion, requested])

    if (!active) return IDLE
    return state.key === requested ? state.value : LOADING
}

/** Test seam — the module cache would otherwise leak between tests. */
export const __resetStandaardOnderbouwingCache = () => documentCache.clear()

export default useStandaardOnderbouwing
